import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Box,
  Text,
  Select,
  RadioGroup,
  Stack,
  Radio,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import { useFetch } from "../../../../hooks/useFetch";
import CustomToast from "../../../../components/CustomToast";
import { useAuth } from "../../../../hooks/useAuth";
import * as XLSX from "xlsx";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface UploadResult {
  total: number;
  success: number;
  failed: number;
  duplicate: number;
  errors: Array<{
    row: number;
    companyName: string;
    reason: string;
  }>;
}

export const BulkUploadModal = ({ isOpen, onClose, onSuccess }: Props) => {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const { loginObj } = useAuth();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [duplicateHandling, setDuplicateHandling] = useState<string>("skip");
  const [assignToUserId, setAssignToUserId] = useState<string>("");
  const [users, setUsers] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [countryCodes, setCountryCodes] = useState<any[]>([]);
  const [preloadedCompanies, setPreloadedCompanies] = useState<any[]>([]);

  // State for upload process
  const [uploading, setUploading] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load dependency data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadDependencyData();
      resetState();
    }
  }, [isOpen]);

  const resetState = () => {
    setSelectedFile(null);
    setDuplicateHandling("skip");
    setUploadResult(null);
    setUploading(false);
    setCurrentProgress(0);
  };

  const loadDependencyData = async () => {
    try {
      // 1. Fetch Users
      const usersRes = await fetchApi("Dropdown/users", "GET");
      if (usersRes) {
        const formatted = (usersRes as any[]).map((item) => ({
          id: item.userId,
          name: item.userName,
        }));
        setUsers(formatted);
        // Default to logged-in user if available
        const currentUserId = loginObj?.userId || "";
        if (formatted.some((u) => Number(u.id) === Number(currentUserId))) {
          setAssignToUserId(String(currentUserId));
        } else if (formatted.length > 0) {
          setAssignToUserId(String(formatted[0].id));
        }
      }
    } catch (error) {
      console.error("Failed to load bulk upload dependencies:", error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const cleanHeader = (header: string): string => {
    return header.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      addToast({ message: "Please select an Excel file", status: "warning" });
      return;
    }

    const checkRes = await fetchApi("Status/CheckActiveUpload", "GET");
    if (checkRes && checkRes.isProcessing) {
      addToast({
        message: "A bulk upload is already in progress. Please wait until the current upload is completed.",
        status: "error",
      });
      return;
    }

    setUploading(true);
    setUploadResult(null);
    setCurrentProgress(0);

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        
        // Retrieve JSON representation with defaults
        const rawRows = XLSX.utils.sheet_to_json<any>(sheet, { defval: "" });

        if (rawRows.length === 0) {
          addToast({ message: "Excel sheet is empty", status: "error" });
          setUploading(false);
          return;
        }

        // Map header keys robustly to match backend DTO BulkLeadRowDto
        const formattedRows = rawRows.map((rawRow) => {
          const mappedRow: any = {};
          Object.keys(rawRow).forEach((key) => {
            const cleanKey = cleanHeader(key);
            if (cleanKey === "companyname") mappedRow.CompanyName = String(rawRow[key]).trim();
            else if (cleanKey === "companyemail") mappedRow.CompanyEmail = String(rawRow[key]).trim();
            else if (cleanKey === "companyphonenumber" || cleanKey === "companyphone") mappedRow.CompanyPhone = String(rawRow[key]).trim();
            else if (cleanKey === "pocname") mappedRow.PocName = String(rawRow[key]).trim();
            else if (cleanKey === "pocemail") mappedRow.PocEmail = String(rawRow[key]).trim();
            else if (cleanKey === "pocphonenumber" || cleanKey === "pocphone") mappedRow.PocPhone = String(rawRow[key]).trim();
            else if (cleanKey === "country") mappedRow.Country = String(rawRow[key]).trim();
          });
          return mappedRow;
        });

        setCurrentProgress(50);

        const payload = {
          Rows: formattedRows,
          DuplicateHandling: duplicateHandling,
          AssignToUserId: Number(assignToUserId),
        };

        const res = await fetchApi("Lead/BulkUpload", "POST", payload);
        
        setCurrentProgress(100);

        if (res) {
          setUploadResult({
            total: res.total,
            success: res.success,
            failed: res.failed,
            duplicate: res.duplicate,
            errors: (res.errors || []).map((err: any) => ({
              row: err.row,
              companyName: err.companyName,
              reason: err.reason,
            })),
          });

          if (res.success > 0) {
            addToast({
              message: `Successfully processed ${res.success} leads!`,
              status: "success",
            });
            onSuccess();
          }
        }
      } catch (error) {
        addToast({ message: "An error occurred while uploading Excel data.", status: "error" });
      } finally {
        setUploading(false);
      }
    };

    reader.readAsBinaryString(selectedFile);
  };

  const updateProgressBar = (currentIndex: number, total: number) => {
    const progress = Math.round(((currentIndex + 1) / total) * 100);
    setCurrentProgress(progress);
  };

  const handleDownloadReport = () => {
    if (!uploadResult || uploadResult.errors.length === 0) return;

    // Header row
    let csvContent = "Row Number,Company Name,Error/Skip Reason\n";

    uploadResult.errors.forEach((e) => {
      // Escape quotes and format row
      const escapedCompany = `"${e.companyName.replace(/"/g, '""')}"`;
      const escapedReason = `"${e.reason.replace(/"/g, '""')}"`;
      csvContent += `${e.row},${escapedCompany},${escapedReason}\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `bulk_lead_upload_report_${new Date().getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(5px)" />
      <ModalContent borderRadius="20px" boxShadow="2xl" border="1px solid #E5E7EB" bg="white">
        <ModalHeader borderBottom="1px solid #F3F4F6" py={5} px={6}>
          <Text fontSize="lg" fontWeight="700" color="#111827">
            Bulk Upload Leads
          </Text>
          <ModalCloseButton color="gray.400" top="20px" right="20px" />
        </ModalHeader>

        <ModalBody px={6} py={6}>
          {!uploadResult && (
            <Stack spacing={6}>
              {/* File Select */}
              <Box>
                <Text fontSize="sm" fontWeight="600" color="#374151" mb={2}>
                  Select Excel File (.xlsx/.xls)
                </Text>
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  border="2px dashed"
                  borderColor={selectedFile ? "#4F46E5" : "#D1D5DB"}
                  borderRadius="12px"
                  bg={selectedFile ? "#F5F3FF" : "#F9FAFB"}
                  p={6}
                  cursor="pointer"
                  _hover={{ borderColor: "#4F46E5", bg: "#F5F3FF" }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Text fontSize="sm" fontWeight="500" color={selectedFile ? "#4F46E5" : "#4B5563"} textAlign="center">
                    {selectedFile ? selectedFile.name : "Click to select or drag Excel file here"}
                  </Text>
                  <Text fontSize="xs" color="#9CA3AF" mt={1}>
                    Supports .xlsx, .xls formats
                  </Text>
                </Flex>
              </Box>

              {/* Duplicate Handling Option */}
              <Box>
                <Text fontSize="sm" fontWeight="600" color="#374151" mb={2}>
                  Duplicate Lead Action (System Config)
                </Text>
                <RadioGroup onChange={setDuplicateHandling} value={duplicateHandling}>
                  <Stack direction="row" spacing={6}>
                    <Radio value="skip" colorScheme="indigo">
                      <Text fontSize="sm" color="#4B5563">
                        Skip Duplicates
                      </Text>
                    </Radio>
                    <Radio value="update" colorScheme="indigo">
                      <Text fontSize="sm" color="#4B5563">
                        Update Existing Records
                      </Text>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Box>

              {/* Assign Leads Dropdown */}
              <Box>
                <Text fontSize="sm" fontWeight="600" color="#374151" mb={2}>
                  Assign Leads to User
                </Text>
                <Select
                  value={assignToUserId}
                  onChange={(e) => setAssignToUserId(e.target.value)}
                  borderRadius="8px"
                  fontSize="sm"
                  borderColor="#D1D5DB"
                  _focus={{ borderColor: "#4F46E5", boxShadow: "0 0 0 1px #4F46E5" }}
                >
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </Select>
              </Box>

              {/* Real-time Progress Bar */}
              {uploading && (
                <Box>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="xs" fontWeight="600" color="#4F46E5">
                      Uploading & processing...
                    </Text>
                    <Text fontSize="xs" fontWeight="600" color="#4F46E5">
                      {currentProgress}%
                    </Text>
                  </Flex>
                  <Progress
                    value={currentProgress}
                    size="sm"
                    borderRadius="4px"
                    colorScheme="indigo"
                    hasStripe
                    isAnimated
                  />
                </Box>
              )}
            </Stack>
          )}

          {/* Results Summary Screen */}
          {uploadResult && (
            <Stack spacing={6}>
              <Box bg="#F9FAFB" p={4} borderRadius="12px" border="1px solid #E5E7EB">
                <Text fontSize="sm" fontWeight="700" color="#374151" mb={3}>
                  Import Summary
                </Text>
                <Table variant="simple" size="sm">
                  <Tbody>
                    <Tr>
                      <Td py={2} pl={0}>Total Records Found</Td>
                      <Td py={2} textAlign="right" fontWeight="700">{uploadResult.total}</Td>
                    </Tr>
                    <Tr>
                      <Td py={2} pl={0}>
                        <Flex align="center" gap={1.5}>
                          <Box w="8px" h="8px" borderRadius="full" bg="green.500" />
                          Successfully Imported
                        </Flex>
                      </Td>
                      <Td py={2} textAlign="right" color="green.600" fontWeight="700">
                        {uploadResult.success}
                      </Td>
                    </Tr>
                    <Tr>
                      <Td py={2} pl={0}>
                        <Flex align="center" gap={1.5}>
                          <Box w="8px" h="8px" borderRadius="full" bg="red.500" />
                          Failed / Invalid
                        </Flex>
                      </Td>
                      <Td py={2} textAlign="right" color="red.600" fontWeight="700">
                        {uploadResult.failed}
                      </Td>
                    </Tr>
                    <Tr>
                      <Td py={2} pl={0}>
                        <Flex align="center" gap={1.5}>
                          <Box w="8px" h="8px" borderRadius="full" bg="orange.400" />
                          Duplicate Skipped
                        </Flex>
                      </Td>
                      <Td py={2} textAlign="right" color="orange.600" fontWeight="700">
                        {uploadResult.duplicate}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>

              {/* Error Log Table */}
              {uploadResult.errors.length > 0 && (
                <Box>
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontSize="xs" fontWeight="700" color="#4B5563">
                      Skipped & Failed Records Details
                    </Text>
                    <Button
                      size="xs"
                      leftIcon={<DownloadIcon />}
                      onClick={handleDownloadReport}
                      colorScheme="red"
                      variant="outline"
                      borderRadius="6px"
                    >
                      Download Error Report
                    </Button>
                  </Flex>
                  <Box maxH="160px" overflowY="auto" border="1px solid #E5E7EB" borderRadius="8px">
                    <Table size="sm" variant="striped">
                      <Thead position="sticky" top={0} bg="white" zIndex={1}>
                        <Tr>
                          <Th fontSize="10px">Row</Th>
                          <Th fontSize="10px">Company</Th>
                          <Th fontSize="10px">Reason</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {uploadResult.errors.map((e, idx) => (
                          <Tr key={idx}>
                            <Td fontSize="11px" py={1.5}>{e.row}</Td>
                            <Td fontSize="11px" fontWeight="600" maxW="120px" isTruncated py={1.5}>
                              {e.companyName}
                            </Td>
                            <Td fontSize="11px" color="red.500" py={1.5}>{e.reason}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </ModalBody>

        <ModalFooter borderTop="1px solid #F3F4F6" py={4} px={6} gap={3}>
          {!uploadResult ? (
            <>
              <Button
                variant="ghost"
                onClick={onClose}
                isDisabled={uploading}
                fontSize="sm"
                h="38px"
                borderRadius="8px"
              >
                Cancel
              </Button>
              <Button
                bg="#4048D6"
                color="white"
                _hover={{ bg: "#353CC4" }}
                onClick={handleUpload}
                isLoading={uploading}
                isDisabled={!selectedFile}
                fontSize="sm"
                h="38px"
                borderRadius="8px"
                px={5}
              >
                Upload & Import
              </Button>
            </>
          ) : (
            <Button
              bg="#4048D6"
              color="white"
              _hover={{ bg: "#353CC4" }}
              onClick={onClose}
              fontSize="sm"
              h="38px"
              borderRadius="8px"
              w="full"
            >
              Close Results
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkUploadModal;
